import React from 'react'
import { TabList, Tab, TabPanel, useTabState, Text, csx } from '@vtex/admin-ui'
import {
  Installment,
  InstallmentOption,
} from '@/checkout-instore-legacy/product/typings'
import { useMediaQuery } from '@/checkout-instore-legacy/hooks/mediaQuery'
import { useIntl } from '@/checkout-instore-legacy/hooks/intl'
import Price from '@/checkout-instore-legacy/components/Commons/Price'

type InstallmentRowProps = {
  sellingPriceCents: number
} & InstallmentOption

const InstallmentRow = (props: InstallmentRowProps) => {
  const intl = useIntl()

  const getNoInterestText = () => {
    const discountPercentage =
      props.quantity === 1
        ? ((props.sellingPriceCents - props.value) * 100) /
          props.sellingPriceCents
        : 0

    return discountPercentage === 0
      ? intl.formatMessage({ id: 'interestFree' }).toLowerCase()
      : intl.formatMessage(
          { id: 'percentageDiscount' },
          { value: `${discountPercentage}%` }
        )
  }

  return (
    <div
      className={csx({
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row',
        paddingY: '$space-6',
        marginX: '$space-6',
        ':not(:last-child)': {
          borderBottom: '$neutral',
        },
      })}
    >
      <div
        className={csx({
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
        })}
      >
        <Text variant="action2">{`${props.quantity}x`}</Text>
        <Text variant="title1">
          {/* // TODO: this error popped up when moving the checkout-instore project
          // to checkout-instore-legacy, but needs to be fixed at some point
          // @ts-ignore */}
          <Price value={Math.floor(props.value)} />
        </Text>
      </div>
      <Text className={csx({ textTransform: 'lowercase' })}>
        {props.interestRate === 0 ? (
          <Text variant="action1" tone="positive">
            {getNoInterestText()}
          </Text>
        ) : (
          <Text variant="action1">
            {intl.formatMessage(
              { id: 'feePercentage' },
              { value: `${props.interestRate}%` }
            )}
          </Text>
        )}
      </Text>
    </div>
  )
}

type InstallmentsContentProps = {
  installments: Installment[]
  sellingPriceCents: number
}

export const InstallmentsContent = (props: InstallmentsContentProps) => {
  const state = useTabState()

  const { value: isMobile } = useMediaQuery({
    phone: true,
    tablet: true,
    desktop: false,
  })

  return (
    <div className={csx({ maxWidth: '100vw' })}>
      <TabList
        state={state}
        aria-label="Installments"
        className={csx({
          whiteSpace: 'nowrap',
          overflow: 'auto',
          borderBottom: '$neutral',
          '> div': {
            flexWrap: 'nowrap !important' as any,
          },
        })}
      >
        {props.installments.map((installment, installmentIndex) => (
          <Tab
            data-testid={`installments-tab-${installmentIndex}`}
            id={installment.payment.id.toString()}
            key={installment.payment.id}
          >
            {installment.payment.name}
          </Tab>
        ))}
      </TabList>
      {props.installments.map((installment, installmentIndex) => (
        <TabPanel
          state={state}
          data-testid={`installments-tab-panel-${installmentIndex}`}
          id={installment.payment.id.toString()}
          key={installment.payment.id}
          className={csx({
            maxHeight: isMobile ? '22.5rem' : 'auto',
            overflow: 'auto',
          })}
        >
          {installment.options.map((option) => (
            <InstallmentRow
              key={option.value}
              sellingPriceCents={props.sellingPriceCents}
              {...option}
            />
          ))}
        </TabPanel>
      ))}
    </div>
  )
}
